<template>
    <modal size="md" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title" v-if="plan">Edit Insurance Plan Information</h3>
            <h3 class="modal-title" v-else>Add Insurance Plan Information</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Employee: </span>{{ employee.full_name }}</div>
                    <div><span class="text-muted">Company: </span>{{ employee.company.name }}</div>
                    <div v-if="plan"><span class="text-muted">Plan: </span>{{ selectedInsurancePlan?.plan_name }}</div>
                </div>
            </div>

            <div class="form-row" v-if="!plan">
                <div class="col">
                    <form-input label="Plan" v-model="formData.insurance_plan" :errors="errors.insurance_plan" type="select" :options="insurancePlanOptions"/>
                </div>
            </div>

            <div class="form-row">
                <div class="col">
                    <form-input label="Tier" v-model="formData.tier" :errors="errors.tier" type="select" :options="tierOptions" :noblank="true" />
                </div>
            </div>

            <div class="form-row" v-if="isAgeBandPlan && planEmployeeCost === null">
                <div class="col">
                    <p class="mb-1">
                        This plan is priced by age band, and this employee's cost cannot be worked out.
                        <span v-if="!employee.dob">Add their date of birth to enroll them.</span>
                    </p>
                </div>
            </div>

            <div class="form-row mb-2">
                <div class="col">
                    <form-input
                        label="Override cost for this employee"
                        v-model="formData.has_custom_employee_rate"
                        :errors="errors.has_custom_employee_rate"
                        type="checkbox"
                    />
                </div>
            </div>

            <div class="form-row">
                <div class="col">
                    <form-input
                        label="Cost to Employee"
                        v-model="employeeRate"
                        :errors="errors.employee_monthly_rate"
                        type="currency"
                        :readonly="!formData.has_custom_employee_rate"
                        :helper-text="employeeRateHelperText"
                    />
                </div>
            </div>

            <div class="form-row mt-4">
                <div class="col">
                    <form-input label="Reason for Change" v-model="formData.reason_for_change" :errors="errors.reason_for_change" type="select" :options="changeReasonOptions" />
                </div>
            </div>

            <div class="form-row">
                <div class="col col-6">
                    <form-input label="Effective Date" v-model="formData.effective_on" :errors="errors.effective_on" type="date" />
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>

                <button type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import moment from 'moment-timezone'

export default {
    props: ['client', 'employee', 'plan', 'futureMutations', 'insurancePlans', ],
    mixins: [FormMixin, ModalMixin],
    components: {},
    emits: ['updated', 'close', ],
    data() {
        const dateValidation = {dateOnly: true}

        if (this.plan) {
            if (this.plan.effective_on) {
                dateValidation.earliest = moment(this.plan.effective_on).format('YYYY-MM-DD')
            }
            else {
                dateValidation.earliest = moment().tz('America/New_York').format('YYYY-MM-DD')
            }
        }

        const data = {
            formData: this.makeFormData(),
            formRules: {
                tier: {presence: {allowEmpty: false}, },
                reason_for_change: {presence: {allowEmpty: false}},
                effective_on: {presence: {allowEmpty: false}, date: dateValidation},
            },
            formLabels: {
                employee_monthly_rate: 'Cost to Employee',
            },
        }

        if (this.plan) {
            data.formRules.insurance_plan = {presence: {allowEmpty: false}}
        }

        return data
    },
    computed: {
        company() {
            return this.client.companies.find(c => c.id == this.employee.company_id)
        },
        insurancePlanOptions() {
            const result = []

            this.insurancePlans.forEach(p => {
                result.push({text: p.plan_name, value: p.id})
            })

            result.sort((a, b) => a.text > b.text ? 1 : -1)

            return result
        },
        selectedInsurancePlan() {
            const planId = this.formData.insurance_plan || this.plan?.insurance_plan_id || null
            if (!planId) {return null}

            return this.insurancePlans.find(p => p.id == planId)
        },
        isAgeBandPlan() {
            return this.selectedInsurancePlan?.billing_type == 'age-band'
        },
        // Mirrors InsurancePlan.get_age_band_date() so the modal can preview the cost.
        // The server resolves the band again on save and is the authority on it.
        ageBandDate() {
            const plan = this.selectedInsurancePlan
            if (!plan) {return null}

            if (plan.age_determination_method == 'explicit') {
                return plan.explicit_age_band_date ? moment(plan.explicit_age_band_date) : null
            }

            if (plan.age_determination_method == 'plan-start') {
                return moment(plan.starts_on)
            }

            if (plan.age_determination_method == 'year-start') {
                return moment().tz('America/New_York').startOf('year')
            }

            return null
        },
        ageForBanding() {
            if (!this.ageBandDate || !this.employee.dob) {return null}

            return this.ageBandDate.diff(moment(this.employee.dob), 'years')
        },
        // An age past the highest band takes that band, and one below the lowest takes
        // the lowest, matching InsurancePlan.get_age_band_for().
        selectedAgeBand() {
            const bands = this.selectedInsurancePlan?.insurance_plan_age_bands || []
            if (this.ageForBanding === null || !bands.length) {return null}

            const sorted = [...bands].sort((a, b) => a.age - b.age)
            let match = null

            sorted.forEach(band => {
                if (band.age <= this.ageForBanding) {
                    match = band
                }
            })

            return match || sorted[0]
        },
        planEmployeeCost() {
            if (!this.selectedInsurancePlan) {return null}

            if (this.isAgeBandPlan) {
                return this.selectedAgeBand ? this.selectedAgeBand.employee_cost : null
            }

            const costsByTier = {
                'single': 'single_employee_cost',
                'single-plus-1': 'single_plus_one_employee_cost',
                'single-plus-children': 'single_plus_children_employee_cost',
                'family': 'family_employee_cost',
            }

            return this.selectedInsurancePlan[costsByTier[this.formData.tier]]
        },
        // Read-only until the cost is being overridden, so the field shows what the plan
        // charges rather than sitting empty.
        employeeRate: {
            get() {
                if (this.formData.has_custom_employee_rate) {
                    return this.formData.employee_monthly_rate
                }
                return this.planEmployeeCost
            },
            set(val) {
                this.formData.employee_monthly_rate = val
            },
        },
        employeeRateHelperText() {
            if (this.formData.has_custom_employee_rate) {return ''}

            if (this.isAgeBandPlan && this.ageForBanding !== null) {
                return `From this plan's age ${this.ageForBanding} band.`
            }

            return 'From the plan.'
        },
        tierOptions() {
            const result = [{text: 'Single', value: 'single'}]

            if (this.selectedInsurancePlan?.has_single_plus_one) {
                result.push({text: 'Single + 1', value: 'single-plus-1'})
            }

            if (this.selectedInsurancePlan?.has_single_plus_children) {
                result.push({text: 'Single + Children', value: 'single-plus-children'})
            }

            if (this.selectedInsurancePlan?.has_family) {
                result.push({text: 'Family', value: 'family'})
            }

            return result
        },
        changeReasonOptions() {
            return [
                {text: 'Open Enrollment', value: 'open-enrollment'},
                {text: 'Family Status Change', value: 'family-status-change'},
                {text: 'Change in Residency', value: 'change-in-residency'},
                {text: 'Employment/Coverage Change', value: 'employment-coverage-change'},
                {text: 'Natural Disaster or Emergency', value: 'natural-disaster-or-emergency'},
            ]
        },
    },
    watch: {
        // Checking the box hands the plan's own cost over as the starting point.
        'formData.has_custom_employee_rate'(val) {
            if (val && !this.formData.employee_monthly_rate) {
                this.formData.employee_monthly_rate = this.planEmployeeCost
            }
        },
    },
    methods: {
        makeFormData() {
            const data = {
                insurance_plan: this.plan ? this.plan.insurance_plan_id : '',
                tier: this.plan ? this.plan.tier : 'single',
                employee_monthly_rate: this.plan ? this.plan.employee_monthly_rate : '',
                has_custom_employee_rate: this.plan ? this.plan.has_custom_employee_rate : false,
                reason_for_change: '',
                effective_on: this.plan?.effective_on ? this.plan.effective_on : moment().format('YYYY-MM-DD'),
            }

            return data
        },
        getFormRules() {
            const rules = JSON.parse(JSON.stringify(this.formRules))

            if (!this.plan) {
                rules.insurance_plan = {presence: {allowEmpty: false}, }
            }

            if (this.formData.has_custom_employee_rate) {
                rules.employee_monthly_rate = {presence: {allowEmpty: false}}
            }

            return rules
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            const data = this.getFormData()

            this.$store.dispatch('START_LOADING')
            return this.$api.post(`/clients/${this.client.id}/employees/${this.employee.id}/insurance-plan-mutations`, data).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully saved insrance plan information.`)
                this.$emit('updated', resp)
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = (errors.__all__ || [])
                this.$bus.showError(errors.__all__)
            })
        }
    }
}
</script>
